import React, { useCallback, useEffect, useRef, useState} from 'react';
import PropTypes, { node } from 'prop-types';
import classNames from 'classnames';

import SlideNavigation from '../containers/SlideNavigation';
import getLogoProps from '../getLogoProps';
import Logo from './Logo.jsx';
import InfoConsoleButton from './InfoConsole/InfoConsoleButton.jsx';
import getReadStatusKeyName from '../utils/getReadStatusKeyName';
import useScrollProgress from '../hooks/useScrollProgress';

import './DeckDock.css';

const DeckLink = ({ name, canEditFolder, deckEditUrl }) => {
  if (!canEditFolder) {
    return name;
  }
  return (
    <a href={deckEditUrl}>{name}</a>
  );
};

DeckLink.propTypes = {
  name: PropTypes.node,
  canEditFolder: PropTypes.bool,
  deckEditUrl: PropTypes.string
};

const DeckDockScrollProgress = () => {
  const barNode = useRef(null);
  const nextLayout = useRef(null);
  const cleanup = useRef(null);

  // Bypass render cycle, as scroll events can be triggered very often
  const onProgress = useCallback((progress) => {
    cleanup.current && cleanup.current();

    nextLayout.current = requestAnimationFrame(() => {
      if (barNode.current) {
        barNode.current.style.transform = `scaleX(${progress})`;
      }
    });

    cleanup.current = () => {
      cancelAnimationFrame(nextLayout.current);
    };
  }, [ barNode ]);

  const { onViewportNode } = useScrollProgress(onProgress);

  useEffect(() => {
    onViewportNode(null);
  }, []);

  useEffect(() => () => {
    cleanup.current && cleanup.current();
  }, []);

  return (
    <div className="DeckDock__scrollProgress">
      <div className="DeckDock__scrollProgress__bar" ref={barNode} />
    </div>
  );
};

const DeckDock = ({
  invite,
  name,
  slideCount,
  slideIndex,
  toggleInfoConsole,
  canEditFolder,
  options = {},
  deckEditUrl,
  theme = {},
  uiMode
}) => {
  const [ hasReadInvite, setHasReadInvite ] = useState(false);

  useEffect(() => {
    setHasReadInvite(
      localStorage.getItem(getReadStatusKeyName(invite))
    );
  }, [ invite ]);

  return (
    <div className={classNames('DeckDock', {
      'DeckDock--primaryColor': theme && theme.usePrimaryHeader,
      [`DeckDock--${uiMode}`]: true
    })}>
      <DeckDockScrollProgress />
      <div className="DeckDock__items">
        <div className={classNames('DeckDock__item', {
          'DeckDock__item--header': true
        })}>
          <div className="DeckDock__header">
            <div className="DeckDock__logo">
              <Logo {...getLogoProps(options)} isSquare={uiMode === 'scroll'} />
            </div>
            {name && (
              <div className="DeckDock__title">
                <DeckLink
                  name={name}
                  canEditFolder={canEditFolder}
                  deckEditUrl={deckEditUrl}
                />
              </div>
            )}
          </div>
        </div>
        <div className={classNames('DeckDock__item', {
          'DeckDock__item--slide-navigation': true
        })}>
          <SlideNavigation
            slideCount={slideCount}
            slideIndex={slideIndex}
            theme={theme}
          />
        </div>
        <div className={classNames('DeckDock__item', {
          'DeckDock__item--actions': true
        })}>
          <InfoConsoleButton
            toggleInfoConsole={toggleInfoConsole}
            invite={invite}
            theme={theme}
          />
        </div>
      </div>
    </div>
  );
};

DeckDock.propTypes = {
  invite: PropTypes.object,
  name: PropTypes.string,
  slideCount: PropTypes.number.isRequired,
  slideIndex: PropTypes.number.isRequired,
  toggleInfoConsole: PropTypes.func.isRequired,
  canEditFolder: PropTypes.bool.isRequired,
  deckEditUrl: PropTypes.string.isRequired,
  options: PropTypes.object.isRequired,
  theme: PropTypes.object,
  uiMode: PropTypes.string.isRequired
};

export default DeckDock;
